import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/20/solid";
import {
  dismissNotification,
  markNotificationRead,
} from "../../redux/notificationSlice";
import NotificationAvatar from "./NotificationAvatar";
import { toastStatusForType, toastStatusToneClass } from "./notificationStatus";

const TOAST_LIMIT = 4;
const AUTO_DISMISS_MS = 9000;

function ToastCard({ n }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { label: statusLabel, tone } = toastStatusForType(n.type);

  useEffect(() => {
    const tid = window.setTimeout(() => {
      dispatch(dismissNotification(n.id));
    }, AUTO_DISMISS_MS);
    return () => window.clearTimeout(tid);
  }, [n.id, dispatch]);

  return (
    <div className="rp-notify-toast">
      <div className="rp-notify-toast__row">
        <div className="rp-notify-toast__accent" aria-hidden="true" />
        <div className="flex shrink-0 items-start py-3 pl-3">
          <div className="rp-notify-toast__avatar-tile">
            <NotificationAvatar url={n.avatarUrl} label={n.actorName} className="h-9 w-9" />
          </div>
        </div>
        <button
          type="button"
          className="min-w-0 flex-1 py-3 pr-1 pl-3 text-left transition-opacity hover:opacity-95"
          onClick={() => {
            dispatch(markNotificationRead(n.id));
            if (n.link) navigate(n.link);
            dispatch(dismissNotification(n.id));
          }}
        >
          {n.actorName ? (
            <p className="text-[11px] font-semibold uppercase tracking-wide text-wa-muted">
              Van {n.actorName}
            </p>
          ) : null}
          <p
            className={`text-[15px] font-semibold leading-snug tracking-tight text-wa-text ${n.actorName ? "mt-1" : ""}`}
          >
            {n.title}
          </p>
          {n.message ? (
            <p className="mt-1 line-clamp-2 text-[13px] leading-relaxed text-wa-muted">{n.message}</p>
          ) : null}
          {n.link ? (
            <p className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-wa-accent">
              Openen
              <span aria-hidden="true" className="text-wa-accent">
                →
              </span>
            </p>
          ) : null}
        </button>
        <button
          type="button"
          className="rp-notify-toast__close"
          aria-label="Sluiten"
          onClick={() => dispatch(dismissNotification(n.id))}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="rp-notify-status-bar">
        <span className="rp-notify-status-bar__label">Status</span>
        <span className={`text-xs font-semibold ${toastStatusToneClass(tone)}`}>{statusLabel}</span>
      </div>
    </div>
  );
}

const NotificationToastStack = () => {
  const items = useSelector((state) => state.notifications.items);
  const toasts = items.slice(0, TOAST_LIMIT);

  if (toasts.length === 0) return null;

  return (
    <div
      className="rp-notify-toast-stack pointer-events-none fixed inset-x-0 top-20 z-[60] flex flex-col items-end px-3 sm:px-6"
      aria-live="polite"
      aria-relevant="additions"
    >
      {toasts.map((n) => (
        <ToastCard key={n.id} n={n} />
      ))}
    </div>
  );
};

export default NotificationToastStack;
